import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from 'core/store/authStore';
import { getMyDriverSession } from 'features/driver-sessions/services/driverSessionApi';
import { queryKeys } from 'core/query/queryKeys';
import { useDriverSessionLocation } from 'shared/hooks/useDriverSessionLocation';
import {
  listMyAssignedOrders,
  releaseOrder,
  updateOrderStatus,
} from 'features/provider/services/providerOrderApi';
import { ROUTES } from 'core/router/routes';
import { useUIStore } from 'core/store/uiStore';
import type { OrderResponse } from 'types/api.generated';

const NEXT_STATUS: Record<string, { label: string; value: 'picked_up' | 'in_transit' | 'delivered' }> = {
  accepted:   { label: 'Mark as Picked Up',  value: 'picked_up'  },
  picked_up:  { label: 'Mark In Transit',    value: 'in_transit' },
  in_transit: { label: 'Mark as Delivered',  value: 'delivered'  },
};

const STATUS_BADGE: Record<string, string> = {
  awaiting_payment: 'bg-amber-50 text-amber-600',
  accepted:   'bg-blue-50 text-blue-600',
  picked_up:  'bg-amber-50 text-amber-600',
  in_transit: 'bg-primary/10 text-primary',
  delivered:  'bg-green-50 text-green-600',
  cancelled:  'bg-red-50 text-red-500',
};

export default function ActiveDelivery() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const firstName = user?.displayName?.split(' ')[0] ?? 'Provider';

  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await listMyAssignedOrders({ perPage: 50 });
      // Only show active (non-terminal) orders at the top
      const active = res.orders.filter(
        (o) => o.status !== 'delivered' && o.status !== 'cancelled',
      );
      const completed = res.orders.filter((o) => o.status === 'delivered');
      setOrders([...active, ...completed]);
    } catch {
      showToast('Failed to load your deliveries.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 5 s while there are non-terminal orders so payment confirmation
  // (awaiting_payment → accepted) is reflected without a manual refresh.
  const hasActiveOrders = orders.some(
    (o) => o.status !== 'delivered' && o.status !== 'cancelled',
  );
  useEffect(() => {
    if (!hasActiveOrders) return;
    const timer = setInterval(() => { void fetchOrders(); }, 5_000);
    return () => clearInterval(timer);
  }, [hasActiveOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionQuery = useQuery({
    queryKey: queryKeys.driverSessions.me,
    queryFn: getMyDriverSession,
  });
  const isOnline = sessionQuery.data?.status === 'online';
  const hasActiveTransitDelivery = useMemo(
    () =>
      orders.some((o) => o.status === 'picked_up' || o.status === 'in_transit'),
    [orders],
  );
  useDriverSessionLocation(isOnline, {
    activeDelivery: hasActiveTransitDelivery,
  });

  const handleRelease = async (orderId: string) => {
    if (!window.confirm("Release this job? Dispatch will find another provider.")) return;
    setReleasing(orderId);
    try {
      await releaseOrder(orderId);
      showToast('Job released.', 'success');
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? 'Could not release order.';
      showToast(msg, 'error');
    } finally {
      setReleasing(null);
    }
  };

  const handleStatusUpdate = async (orderId: string, next: 'picked_up' | 'in_transit' | 'delivered') => {
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, next);
      showToast('Status updated!', 'success');
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: next } : o)),
      );
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Could not update status.';
      showToast(msg, 'error');
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const activeOrders = orders.filter((o) => o.status !== 'delivered' && o.status !== 'cancelled');
  const completedOrders = orders.filter((o) => o.status === 'delivered');

  return (
    <div className="min-h-screen bg-background-light font-display text-slate-900">
      {/* ── Top Nav ── */}
      <header className="sticky top-0 z-50 bg-background-light/85 backdrop-blur-xl border-b border-primary/8 px-6 lg:px-20 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to={ROUTES.PROVIDER.DASHBOARD} className="flex items-center gap-2.5">
            <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
              <span className="material-symbols-outlined text-white text-[22px]" style={{ fontVariationSettings: '"FILL" 1' }}>
                directions_run
              </span>
            </div>
            <h2 className="text-[1.4rem] font-black tracking-tighter">Errandi</h2>
          </Link>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2.5 bg-white border border-slate-100 rounded-xl px-3.5 py-2 shadow-sm">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-black text-sm">{firstName[0]?.toUpperCase()}</span>
              </div>
              <span className="text-sm font-semibold text-slate-700">{firstName}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors px-3 py-2"
            >
              <span className="material-symbols-outlined text-[18px]">logout</span>
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to={ROUTES.PROVIDER.DASHBOARD} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary transition-colors mb-2">
              <span className="material-symbols-outlined text-[16px]">arrow_back</span>
              Dashboard
            </Link>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">My Deliveries</h1>
            <p className="text-sm text-slate-500 mt-1">Update status as you go.</p>
          </div>
          <button
            onClick={fetchOrders}
            disabled={loading}
            className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary/40 hover:text-primary transition-all shadow-sm disabled:opacity-50"
          >
            <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>refresh</span>
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <span className="material-symbols-outlined text-4xl text-primary animate-spin">progress_activity</span>
          </div>
        )}

        {!loading && orders.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
            <span className="material-symbols-outlined text-5xl text-slate-200 mb-4 block" style={{ fontVariationSettings: '"FILL" 1' }}>local_shipping</span>
            <p className="text-slate-700 font-semibold mb-1">No deliveries yet</p>
            <Link to={ROUTES.PROVIDER.DASHBOARD} className="text-sm text-primary font-semibold hover:underline">Go to dashboard →</Link>
          </div>
        )}

        {/* Active deliveries */}
        {!loading && activeOrders.length > 0 && (
          <section className="mb-10">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">Active</h2>
            <div className="space-y-4">
              {activeOrders.map((order) => {
                const next = NEXT_STATUS[order.status];
                return (
                  <div key={order.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${STATUS_BADGE[order.status] ?? 'bg-slate-100 text-slate-600'}`}>
                            {order.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex flex-col items-center pt-1 gap-1">
                            <span className="material-symbols-outlined text-primary text-[16px]" style={{ fontVariationSettings: '"FILL" 1' }}>location_on</span>
                            <div className="w-0.5 h-3 bg-slate-200" />
                            <span className="material-symbols-outlined text-green-500 text-[16px]" style={{ fontVariationSettings: '"FILL" 1' }}>flag</span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-700">{order.pickupAddress}</p>
                            <p className="text-sm font-semibold text-slate-700 mt-1">{order.deliveryAddress}</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">
                          {order.recipientFirstName} {order.recipientLastName} · {order.recipientPhone}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {order.status === 'accepted' && (
                          <button
                            type="button"
                            onClick={() => handleRelease(order.id)}
                            disabled={releasing === order.id || updating === order.id}
                            className="text-sm font-semibold text-slate-500 hover:text-red-600 disabled:opacity-50"
                          >
                            {releasing === order.id ? 'Releasing…' : "Can't complete this job"}
                          </button>
                        )}
                        {next && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, next.value)}
                            disabled={updating === order.id || releasing === order.id}
                            className="flex items-center gap-2 bg-primary text-white text-sm font-bold px-5 py-3 rounded-xl hover:bg-primary/90 active:scale-95 transition-all shadow-sm shadow-primary/30 disabled:opacity-60"
                          >
                            {updating === order.id ? (
                              <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
                            ) : (
                              <>
                                <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                                {next.label}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Completed deliveries */}
        {!loading && completedOrders.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">Completed</h2>
            <div className="space-y-3">
              {completedOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 opacity-75">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{order.pickupAddress} → {order.deliveryAddress}</p>
                      <p className="text-xs text-slate-400 mt-0.5">₦{order.amount.toLocaleString()}</p>
                    </div>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-green-50 text-green-600">Delivered</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
